import React, { useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { CameraAlt, CheckCircle, Clear, Search } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { uploadPhoto, useAllActiveCheckins, walkInCheckIn } from './services';
import { CheckInRecord } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
  adminId: string;
}

interface WalkInFormState {
  childFirstName: string;
  childLastName: string;
  dateOfBirth: Date | null;
  parentName: string;
  parentPhone: string;
  allergies: string;
  specialNotes: string;
  photoUrl: string | null;
}

const EMPTY_FORM: WalkInFormState = {
  childFirstName: '',
  childLastName: '',
  dateOfBirth: null,
  parentName: '',
  parentPhone: '',
  allergies: '',
  specialNotes: '',
  photoUrl: null,
};

const WalkInDialog: React.FC<Props> = ({ open, onClose, adminId }) => {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { checkins, loading } = useAllActiveCheckins(false);

  const pastChildren = useMemo(() => {
    const seen = new Set<string>();
    return checkins.filter((c) => {
      const key = c.childId ?? `${c.childFirstName}_${c.childLastName}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [checkins]);

  const filteredPast = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pastChildren;
    return pastChildren.filter((c) =>
      `${c.childFirstName} ${c.childLastName} ${c.parentName ?? ''}`
        .toLowerCase()
        .includes(q)
    );
  }, [pastChildren, search]);

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setSearch('');
    setError('');
    setTab(0);
    onClose();
  };

  const handlePhotoCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((f) => ({ ...f, photoUrl: ev.target?.result as string }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleNewCheckIn = async () => {
    if (!form.childFirstName.trim() || !form.childLastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    setError('');
    setCheckingIn(true);
    try {
      let resolvedPhoto: string | null = null;
      if (form.photoUrl) {
        resolvedPhoto = await uploadPhoto(form.photoUrl);
      }
      await walkInCheckIn({
        childFirstName: form.childFirstName.trim(),
        childLastName: form.childLastName.trim(),
        dateOfBirth: form.dateOfBirth,
        photoUrl: resolvedPhoto,
        parentName: form.parentName.trim() || undefined,
        parentPhone: form.parentPhone.trim() || undefined,
        allergies: form.allergies.trim() || undefined,
        specialNotes: form.specialNotes.trim() || undefined,
        adminId,
      });
      handleClose();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleHistoryCheckIn = async (record: CheckInRecord) => {
    setCheckingIn(true);
    try {
      await walkInCheckIn({
        childFirstName: record.childFirstName,
        childLastName: record.childLastName,
        dateOfBirth: record.dateOfBirth,
        photoUrl: record.photoUrl,
        allergies: record.allergies,
        specialNotes: record.specialNotes,
        parentName: record.parentName,
        parentPhone: record.parentPhone,
        emergencyContacts: record.emergencyContacts,
        adminId,
      });
      handleClose();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setCheckingIn(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{ sx: { mt: 4 } }}
    >
      <DialogTitle>Walk-In Check-In</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="New Child" />
          <Tab label="From History" />
        </Tabs>

        {/* ── New Child Form ── */}
        {tab === 0 && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {/* Photo capture */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="user"
                style={{ display: 'none' }}
                onChange={handlePhotoCapture}
              />
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{ position: 'relative', cursor: 'pointer' }}
              >
                <Avatar
                  src={form.photoUrl ?? undefined}
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: 'primary.main',
                    fontSize: 42,
                  }}
                >
                  {!form.photoUrl &&
                    (form.childFirstName?.[0] ?? (
                      <CameraAlt sx={{ fontSize: 38 }} />
                    ))}
                </Avatar>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    bgcolor: 'primary.dark',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 2,
                  }}
                >
                  <CameraAlt sx={{ fontSize: 15, color: 'white' }} />
                </Box>
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: -1 }}
            >
              Tap to take photo
            </Typography>

            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name *"
                value={form.childFirstName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, childFirstName: e.target.value }))
                }
                fullWidth
                size="small"
                autoFocus
              />
              <TextField
                label="Last Name *"
                value={form.childLastName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, childLastName: e.target.value }))
                }
                fullWidth
                size="small"
              />
            </Stack>
            <DatePicker<Date>
              label="Date of Birth"
              value={form.dateOfBirth}
              onChange={(val: Date | null) =>
                setForm((f) => ({ ...f, dateOfBirth: val }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" />
              )}
            />
            <TextField
              label="Parent Name"
              value={form.parentName}
              onChange={(e) =>
                setForm((f) => ({ ...f, parentName: e.target.value }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label="Parent Phone"
              value={form.parentPhone}
              onChange={(e) =>
                setForm((f) => ({ ...f, parentPhone: e.target.value }))
              }
              fullWidth
              size="small"
              type="tel"
              inputProps={{ maxLength: 15 }}
            />
            <TextField
              label="Allergies"
              value={form.allergies}
              onChange={(e) =>
                setForm((f) => ({ ...f, allergies: e.target.value }))
              }
              fullWidth
              size="small"
              placeholder="e.g. peanuts, dairy"
            />
            <TextField
              label="Special Notes"
              value={form.specialNotes}
              onChange={(e) =>
                setForm((f) => ({ ...f, specialNotes: e.target.value }))
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Stack>
        )}

        {/* ── From History ── */}
        {tab === 1 && (
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch('')}
                      edge="end"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ mb: 1 }}
            />

            {loading && (
              <Stack alignItems="center" sx={{ mt: 3 }}>
                <CircularProgress size={24} />
              </Stack>
            )}

            {!loading && filteredPast.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mt: 3 }}
              >
                {search
                  ? `No results for "${search}"`
                  : 'No previous check-ins found'}
              </Typography>
            )}

            <List dense sx={{ maxHeight: 340, overflow: 'auto' }}>
              {filteredPast.map((record, i) => (
                <React.Fragment key={record.id}>
                  {i > 0 && <Divider component="li" />}
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      record.checkOutTime === null ? (
                        <Chip
                          icon={<CheckCircle fontSize="small" />}
                          label="Checked In"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          disabled={checkingIn}
                          onClick={() => handleHistoryCheckIn(record)}
                          sx={{ borderRadius: 2 }}
                        >
                          {checkingIn ? <CircularProgress size={16} /> : 'Check In'}
                        </Button>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={record.photoUrl ?? undefined}
                        sx={{ width: 40, height: 40 }}
                      >
                        {record.childFirstName?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600}>
                          {record.childFirstName} {record.childLastName}
                        </Typography>
                      }
                      secondary={
                        <>
                          {record.parentName && (
                            <Typography variant="caption" display="block">
                              Parent: {record.parentName}
                            </Typography>
                          )}
                          {record.allergies && record.allergies !== 'N/A' && (
                            <Typography
                              variant="caption"
                              color="warning.dark"
                              display="block"
                            >
                              ⚠ {record.allergies}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={checkingIn}>
          Cancel
        </Button>
        {tab === 0 && (
          <Button
            variant="contained"
            onClick={handleNewCheckIn}
            disabled={checkingIn}
            startIcon={checkingIn ? <CircularProgress size={16} /> : undefined}
          >
            {checkingIn ? 'Checking In…' : 'Check In'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WalkInDialog;
